/**
 * Data Access Layer for Listens (coverage tracking)
 *
 * A `listen` records that a voter (a users row) has heard a happening — written
 * by the player with a resume position (source 'player') or via a manual
 * "I've heard this" override (source 'manual'), indistinguishable at query time.
 * One row per (user, happening); progress upserts the resume position.
 *
 * Coverage is the prerequisite for a good round 1: it surfaces the happenings
 * nobody has actually heard (orphans: fewer than 2 listens) before nominations
 * open. Listens are account-scoped and selection-independent — a listen is
 * reusable across selections; coverage is computed against a selection's scope.
 *
 * The acting user id is passed in explicitly (the API sources it from the
 * request JWT's `sub`); this module only holds the account id in context.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { Listen, ListenUpsert, CoverageRow } from './types.js';

const ORPHAN_THRESHOLD = 2; // "fewer than 2 listens" — see Coverage in the spec.

/**
 * Record (or update) that `userId` has heard a happening. Idempotent per
 * (user, happening): a second call updates the resume position / listened_at.
 * A manual override and a player-written row are identical here.
 */
export async function upsertListen(userId: string, input: ListenUpsert): Promise<Listen> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('upsertListen: no account context set');

  const row = {
    account_id: accountId,
    user_id: userId,
    happening_id: input.happening_id,
    position_seconds: input.position_seconds ?? null,
    source: input.source ?? 'manual',
    ...(input.listened_at ? { listened_at: input.listened_at } : {}),
  };

  const { data, error } = await getClient()
    .from(TABLES.listens)
    .upsert(row, { onConflict: 'user_id,happening_id' })
    .select()
    .single();

  if (error) throw error;
  return data as Listen;
}

/**
 * Remove a listen (e.g. a manual "heard" toggled back off).
 */
export async function deleteListen(userId: string, happeningId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.listens)
    .delete()
    .eq('user_id', userId)
    .eq('happening_id', happeningId);

  if (error) throw error;
}

/**
 * List listens, optionally narrowed to a user and/or a set of happenings.
 * Account scoping is enforced by RLS; the account filter is a belt-and-braces
 * narrowing for the service-role path.
 */
export async function listListens(filters?: {
  user_id?: string;
  happening_ids?: string[];
}): Promise<Listen[]> {
  let query = getClient().from(TABLES.listens).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);
  if (filters?.user_id) query = query.eq('user_id', filters.user_id);
  if (filters?.happening_ids && filters.happening_ids.length > 0) {
    query = query.in('happening_id', filters.happening_ids);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as Listen[];
}

/**
 * Build the coverage grid for a set of candidate happenings: for each, who has
 * heard it and whether it's an orphan (fewer than 2 listens). Happenings with
 * zero listens are still returned (empty listener list) so the caller can render
 * a complete grid. Sorted fewest-listens-first, the order the coverage view wants.
 */
export async function getCoverage(happeningIds: string[]): Promise<CoverageRow[]> {
  const byHappening = new Map<string, Set<string>>();
  for (const id of happeningIds) byHappening.set(id, new Set());

  if (happeningIds.length > 0) {
    const listens = await listListens({ happening_ids: happeningIds });
    for (const l of listens) {
      // Ignore any listen that isn't in the requested candidate set.
      const set = byHappening.get(l.happening_id);
      if (set) set.add(l.user_id);
    }
  }

  const rows: CoverageRow[] = happeningIds.map((happening_id) => {
    const listeners = byHappening.get(happening_id) ?? new Set<string>();
    const listener_user_ids = Array.from(listeners);
    return {
      happening_id,
      listener_user_ids,
      listen_count: listener_user_ids.length,
      is_orphan: listener_user_ids.length < ORPHAN_THRESHOLD,
    };
  });

  rows.sort((a, b) => a.listen_count - b.listen_count);
  return rows;
}

/**
 * Count of orphans (happenings with fewer than 2 listens) in a candidate set.
 * Surfaced prominently on the selection dashboard.
 */
export async function countOrphans(happeningIds: string[]): Promise<number> {
  const coverage = await getCoverage(happeningIds);
  return coverage.filter((c) => c.is_orphan).length;
}
